import { useTranslation } from "react-i18next";
import {
    Box,
    Container,
    SimpleGrid,
    Heading,
    Flex,
    Text,
    Stack,
    HStack,
    Icon,
    Link,
    VisuallyHidden,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    InputGroup,
    InputLeftAddon,
    Button,
    Card,
    CardBody,
    Divider,
    chakra,
} from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { FiMail, FiPhone, FiArrowRight } from "react-icons/fi";
import EventsService from "../../../../services/events/eventsService";
import { useAppToast } from "../../../../hooks/useAppToast";
import InlineErrorInput from "../../../../shared/components/InlineErrorInput/InlineErrorInput";

function ContactItem({ icon, label, value, href }) {
    return (
        <HStack spacing={4} align="start">
            <Icon as={icon} boxSize={5} color="brand.400" mt="2px" />
            <Stack spacing={0}>
                {href ? (
                    <Link href={href} _hover={{ color: "brand.400" }}>
                        {value}
                    </Link>
                ) : (
                    <Text>{value}</Text>
                )}
            </Stack>
        </HStack>
    );
}

function FieldError({ text }) {
    if (!text) return <VisuallyHidden>OK</VisuallyHidden>;
    return (
        <Text mt={1} fontSize="sm" color="red.300">
            {text}
        </Text>
    );
}

const Contact = () => {
    const { t } = useTranslation("default");
    const toast = useAppToast();

    const Schema = Yup.object({
        fullName: Yup.string().trim().required(t('contact.form.name')),
        email: Yup.string().trim().email(t('contact.form.error.email')).required(t('contact.form.email')),
        company: Yup.string().trim().optional(),
        phone: Yup.string().trim().required(t('contact.form.phone')),
        message: Yup.string().trim().optional(),
    });

    const handleSubmit = async (values, { resetForm }) => {
        try {
            const response = await EventsService.contact(values).then(r => r.data);

            toast({
                title: t('contact.send'),
                description: t('contact.thanks'),
                status: "success",
                duration: 4000,
                isClosable: true,
            });
            resetForm();
        } catch (e) {
            toast({
                title: t('contact.error.send'),
                description: t('contact.retry'),
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    return (
        <Box color="#6E6064" minH="100dvh" pb={{ base: 16, md: 24 }}>
            <Container maxW="7xl" pt={{ base: 12, md: 20 }}>
                <Stack spacing={8} textAlign="center" align="center">
                    <Heading
                        as="h1"
                        size="2xl"
                        lineHeight="1.15"
                        letterSpacing="-0.02em"
                        fontFamily="body" fontWeight={'700'} fontSize={'32px'} color={'#1E1517'}
                    >
                        {t('contact.question')}
                        <br />
                        <chakra.span color={'#1E1517'}>{t('contact.disposal')}</chakra.span>
                    </Heading>
                </Stack>

                <SimpleGrid
                    columns={{ base: 1, lg: 2 }}
                    spacing={{ base: 8, md: 12 }}
                    mt={{ base: 12, md: 16 }}
                    alignItems="flex-start"
                >
                    {/* Lewa kolumna -> informacje kontaktowe */}
                    <Card bg="white" borderRadius="2xl" shadow="xl" p={4}>
                        <CardBody>
                            <Stack spacing={6}>
                                <Stack spacing={2}>
                                    <Text
                                        fontSize="xs"
                                        letterSpacing="0.35em"
                                        textTransform="uppercase"
                                    >
                                        {t('contact.get_in_touch')}
                                    </Text>
                                    <Heading size="lg">{t('contact.us')}</Heading>
                                    <Text>
                                        {t('contact.question2')}
                                    </Text>
                                    <Text>
                                        {t('contact.message2')}
                                    </Text>
                                </Stack>

                                <Divider borderColor="gray.700" />

                                <Stack spacing={4} color={'#338a5b'}>
                                    <ContactItem
                                        icon={FiMail}
                                        label={t('contact.label.email')}
                                        value="office@coblit.com"
                                        href="mailto:office@coblit.com"
                                    />
                                    <ContactItem
                                        icon={FiPhone}
                                        label={t('contact.label.phone')}
                                        value="+48 602 444 299"
                                        href="tel:+48602444299"
                                    />
                                    <ContactItem
                                        icon={FiPhone}
                                        label={t('contact.label.phone')}
                                        value="+48 600 242 170"
                                        href="tel:+48600242170"
                                    />
                                </Stack>
                            </Stack>
                        </CardBody>
                    </Card>

                    {/* Prawa kolumna -> formularz */}
                    <Card bg="white" color={'#6E6064'} borderRadius="2xl" shadow="xl" p={4}>
                        <CardBody>
                            <Formik
                                initialValues={{
                                    fullName: "",
                                    email: "",
                                    company: "",
                                    phone: "",
                                    message: "",
                                }}
                                validationSchema={Schema}
                                onSubmit={handleSubmit}
                            >
                                {({ errors, touched, isSubmitting }) => (
                                    <Form noValidate>
                                        <Stack spacing={5}>
                                            <InlineErrorInput
                                                label={t('name')}
                                                name="fullName"
                                                errors={errors.fullName}
                                                touched={touched.fullName}
                                                backgroundColor="white"
                                            />

                                            <InlineErrorInput
                                                label={t('email')}
                                                name="email"
                                                type="email"
                                                errors={errors.email}
                                                touched={touched.email}
                                                backgroundColor="white"
                                            />

                                            <InlineErrorInput
                                                label={t('company.name')}
                                                name="company"
                                                errors={errors.company}
                                                touched={touched.company}
                                                backgroundColor="white"
                                            />

                                            <InlineErrorInput
                                                label={t('phone')}
                                                name="phone"
                                                errors={errors.phone}
                                                touched={touched.phone}
                                                backgroundColor="white"
                                                isRequired
                                                leftAddon="+48"
                                                autoComplete="tel"
                                            />

                                            <InlineErrorInput
                                                label={t('contact.description')}
                                                name="message"
                                                errors={errors.message}
                                                touched={touched.message}
                                                backgroundColor="white"
                                                as={Textarea}
                                                minH="150px"
                                            />

                                            <HStack pt={2} justify="center" w={"full"}>
                                                <Button
                                                    type="submit"
                                                    bg={'#7A1F35'} w={'158px'} h={'59px'} color={"white"} borderRadius={'12px'} gap={'10px'}
                                                    fontFamily="body" fontWeight={'600'} fontSize={'16px'}
                                                    colorScheme="brand.500"
                                                    isLoading={isSubmitting}
                                                    loadingText="Wysyłanie"
                                                >
                                                    {t('send')}
                                                </Button>
                                            </HStack>
                                        </Stack>
                                    </Form>
                                )}
                            </Formik>
                        </CardBody>
                    </Card>
                </SimpleGrid>
            </Container>
        </Box>
    );
}

export default Contact;
